// pattern: imperative-shell
// Web Speech API wrapper — mic button with live transcript feedback

export interface VoiceResult {
	transcript: string;
	confidence: number;
}

type VoiceCallback = (result: VoiceResult) => void;
type InterimCallback = (text: string) => void;

// Feature detection
const RecognitionClass: SpeechRecognitionConstructor | undefined =
	window.SpeechRecognition ?? window.webkitSpeechRecognition;

export function isSpeechSupported(): boolean {
	return !!RecognitionClass;
}

export function renderVoiceButton(
	container: HTMLElement,
	onResult: VoiceCallback,
	onInterim?: InterimCallback,
): void {
	if (!RecognitionClass) {
		container.innerHTML = "";
		return;
	}

	const btn = document.createElement("button");
	btn.type = "button";
	btn.className = "btn btn-secondary voice-btn";
	btn.innerHTML = '<span class="voice-icon">\u{1F3A4}</span> Speak';
	btn.setAttribute("aria-label", "Record voice observation");

	let recognition: SpeechRecognition | null = null;
	let isListening = false;

	function startListening() {
		if (!RecognitionClass) return;
		const rec = new RecognitionClass();
		recognition = rec;
		rec.continuous = false;
		rec.interimResults = !!onInterim;
		rec.lang = "en-US";
		rec.maxAlternatives = 1;

		rec.onresult = (event: SpeechRecognitionEvent) => {
			let finalTranscript = "";
			let interimTranscript = "";

			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i]!;
				const text = result[0]!.transcript;
				if (result.isFinal) {
					finalTranscript += text;
				} else {
					interimTranscript += text;
				}
			}

			if (interimTranscript && onInterim) {
				onInterim(interimTranscript);
			}

			if (finalTranscript) {
				const lastResult = event.results[event.results.length - 1]!;
				onResult({ transcript: finalTranscript, confidence: lastResult[0]!.confidence });
			}
		};

		rec.onend = () => {
			isListening = false;
			btn.innerHTML = '<span class="voice-icon">\u{1F3A4}</span> Speak';
			btn.classList.remove("voice-active");
		};

		rec.onerror = (event: SpeechRecognitionErrorEvent) => {
			isListening = false;
			btn.innerHTML = '<span class="voice-icon">\u{1F3A4}</span> Speak';
			btn.classList.remove("voice-active");

			if (event.error !== "aborted" && event.error !== "no-speech") {
				console.error("Speech recognition error:", event.error);
			}
		};

		rec.start();
		isListening = true;
		btn.innerHTML = '<span class="voice-icon voice-pulse">\u{1F534}</span> Listening...';
		btn.classList.add("voice-active");
	}

	function stopListening() {
		if (recognition) {
			recognition.stop();
			recognition = null;
		}
		isListening = false;
	}

	btn.addEventListener("click", () => {
		if (isListening) {
			stopListening();
		} else {
			startListening();
		}
	});

	container.appendChild(btn);
}
